import { useCallback, useEffect } from 'react';
import { useAccount, useDisconnect } from 'wagmi';
import { useModalState } from '../../hooks/useModalState';
import { useOnboardSteps } from '../../hooks/useOnboardSteps';
import { useActiveSelectedSubaccount } from '../../hooks/useSelectedSubaccount';
import { useSubaccounts } from '../../hooks/useSubaccounts';
import { SubaccountData, useWalletStorage } from '../../hooks/useWalletStorage';
import { DepositModal } from '../DepositModal';
import { EnableTradingModal } from '../EnableTradingModal/EnableTradingModal';
import { TermsModal } from '../TermsModal/TermsModal';

export const OnboardModals = () => {
  const { updateSubaccountData } = useWalletStorage();
  const setTermsModal = useModalState((state) => state.setTermsModal);
  const setDepositModal = useModalState((state) => state.setDepositModal);
  const setSigningMethodModal = useModalState((state) => state.setSigningModal);
  const { address } = useAccount();
  const { disconnect } = useDisconnect();
  const selectedSubaccount = useActiveSelectedSubaccount();
  const { isSuccess: useSubaccountsSuccess } = useSubaccounts(address);
  const { steps, termsAgreedAt, agreeToTerms } = useOnboardSteps();

  const termsStep = steps.find((step) => step.id === 'terms');

  // If terms haven't been agreed to and user is signed in, show terms modal.
  useEffect(() => {
    if (address && useSubaccountsSuccess) {
      if (!termsStep?.completed) {
        termsStep?.activate();
      } else {
        setTermsModal('closed');
      }
    }
  }, [address, useSubaccountsSuccess, setTermsModal, termsStep]);

  const handleEnabledTrading = useCallback(
    (subaccountData: SubaccountData) => {
      if (!address || !selectedSubaccount) return;

      setSigningMethodModal('closed');

      if (subaccountData.signingMethod === 'one-click') {
        updateSubaccountData(address, selectedSubaccount.id, {
          ...subaccountData,
          linked: true,
        });
      }
    },
    [address, setSigningMethodModal, selectedSubaccount, updateSubaccountData]
  );

  return (
    <>
      <TermsModal
        address={address}
        termsAgreedAt={termsAgreedAt}
        onAgreed={() => {
          setTermsModal('closed');
          agreeToTerms();

          const nextStep = steps.findIndex(({ id, completed }) => !completed && id !== 'terms');
          steps[nextStep]?.activate?.();
        }}
        onSignOut={() => {
          setTermsModal('closed');
          disconnect();
        }}
      />
      <DepositModal
        context="Onboard"
        onDeposit={() => {
          setDepositModal({ openOrClosed: 'closed' });

          const nextStep = steps.findIndex(({ id, completed }) => !completed && id !== 'deposit');
          steps[nextStep]?.activate?.();
        }}
      />
      <EnableTradingModal onComplete={handleEnabledTrading} />
    </>
  );
};
